import { atom } from 'recoil';

import { ICommand } from '@chainlit/react-client';
import type { DashboardReference } from '@/lib/dashboardReferences';
import { AgentResponseModel, ManagedAgentResponseModel, McpResponseModel } from '@/customApiClients/mgmtApiClient/Api';

export interface IAttachment {
  id: string;
  serverId?: string;
  name: string;
  size: number;
  type: string;
  uploadProgress?: number;
  uploaded?: boolean;
  cancel?: () => void;
  remove?: () => void;
}

export const dashboardReferencesState = atom<DashboardReference[]>({
  key: 'DashboardReferences',
  default: []
});

export const pendingPromptState = atom<string | null>({
  key: 'PendingPrompt',
  default: null
});

export const queuedPromptState = atom<string | null>({
  key: 'QueuedPrompt',
  default: null
});

export const attachmentsState = atom<IAttachment[]>({
  key: 'Attachments',
  default: []
});

export const persistentCommandState = atom<ICommand | undefined>({
  key: 'PersistentCommand',
  default: undefined
});

export const deepIntelligenceUnreadCountState = atom<number>({
  key: 'DeepIntelligenceUnreadCount',
  default: 0
});

// Helper to get organization-specific localStorage key
const getOrgKey = (baseKey: string): string => {
  const orgId = localStorage.getItem('selected-organization-id') || 'default';
  return `${baseKey}-${orgId}`;
};

// Selected data sources (MCP servers) for current chat session
export const selectedSourcesState = atom<McpResponseModel[]>({
  key: 'SelectedSources',
  default: [],
  effects: [
    ({ setSelf, onSet }) => {
      // Load from localStorage on initialization (organization-specific)
      const storageKey = getOrgKey('chainlit-selected-sources');
      const savedValue = localStorage.getItem(storageKey);
      if (savedValue !== null) {
        try {
          const parsed = JSON.parse(savedValue);
          if (Array.isArray(parsed)) {
            setSelf(parsed);
          }
        } catch (e) {
          console.warn('Failed to parse saved sources from localStorage', e);
        }
      }

      // Save to localStorage whenever the value changes (organization-specific)
      onSet((newValue) => {
        const storageKey = getOrgKey('chainlit-selected-sources');
        localStorage.setItem(storageKey, JSON.stringify(newValue));
      });
    }
  ]
});

// Flag to control whether model selection should be persisted to localStorage
// Set to false during auto-selection, true during explicit user selection
let _shouldPersistModel = true;
export const setShouldPersistModel = (value: boolean) => { _shouldPersistModel = value; };
export const getShouldPersistModel = () => _shouldPersistModel;

// Selected AI model (global, not organization-specific)
export const selectedModelState = atom<AgentResponseModel | undefined>({
  key: 'SelectedModel',
  default: undefined,
  effects: [
    ({ setSelf, onSet }) => {
      // Load from localStorage on initialization (global key)
      const storageKey = 'chainlit-selected-model';
      const savedValue = localStorage.getItem(storageKey);
      if (savedValue !== null && savedValue !== 'undefined' && savedValue !== '') {
        try {
          const parsed = JSON.parse(savedValue);
          if (parsed && typeof parsed === 'object') {
            setSelf(parsed);
          }
        } catch (e) {
          console.warn('Failed to parse saved model from localStorage, clearing it', e);
          localStorage.removeItem(storageKey);
        }
      }

      // Save to localStorage whenever the value changes (only if flag is set)
      onSet((newValue) => {
        if (_shouldPersistModel) {
          const storageKey = 'chainlit-selected-model';
          if (newValue === undefined || newValue === null) {
            // Remove from localStorage instead of saving "undefined"
            localStorage.removeItem(storageKey);
          } else {
            localStorage.setItem(storageKey, JSON.stringify(newValue));
          }
        }
      });
    }
  ]
});

// Composer mode: single agent+MCP ("normal") vs. multi-agent coordinator ("multiagent").
// Locked to 'multiagent' — the 'normal' mode UI (DMSourcesPopover, McpButton,
// DMModelSelector) is kept in the codebase but unreachable, since nothing
// sets this away from the default anymore. No localStorage restore, so old
// sessions with a persisted 'normal' value don't resurrect it.
export const composerModeState = atom<'normal' | 'multiagent'>({
  key: 'ComposerMode',
  default: 'multiagent'
});

// Selected sub-agent ids for multi-agent mode (org-specific), drives the auto-coordinator roster
export const selectedSubAgentsState = atom<string[]>({
  key: 'SelectedSubAgents',
  default: [],
  effects: [
    ({ setSelf, onSet }) => {
      const storageKey = getOrgKey('dm-selected-subagents');
      const saved = localStorage.getItem(storageKey);
      if (saved !== null) {
        try {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) setSelf(parsed);
        } catch (e) {
          console.warn('Failed to parse saved sub-agents', e);
        }
      }
      onSet((newValue) => {
        localStorage.setItem(getOrgKey('dm-selected-subagents'), JSON.stringify(newValue));
      });
    }
  ]
});

// Debug mode state with localStorage persistence
export const debugModeState = atom<boolean>({
  key: 'DebugMode',
  default: false,
  effects: [
    ({ setSelf, onSet }) => {
      // Load from localStorage on initialization
      const savedValue = localStorage.getItem('chainlit-debug-mode');
      if (savedValue !== null) {
        setSelf(savedValue === 'true');
      }

      // Save to localStorage whenever the value changes
      onSet((newValue) => {
        localStorage.setItem('chainlit-debug-mode', String(newValue));
      });
    }
  ]
});

// Global cache of available data sources (MCP servers from catalog)
// Shared across all component instances to prevent duplicate API calls
export const availableDataSourcesState = atom<McpResponseModel[]>({
  key: 'AvailableDataSources',
  default: []
});

// All data sources including hidden ones (used for backend sync payload).
// UI components should use availableDataSourcesState (visible only).
export const allDataSourcesState = atom<McpResponseModel[]>({
  key: 'AllDataSources',
  default: []
});

// Global cache of available AI models/agents
// Shared across all component instances to prevent duplicate API calls
export const availableModelsState = atom<AgentResponseModel[]>({
  key: 'AvailableModels',
  default: []
});

// Global cache of the managed agents the user may pick as sub-agents
// (coordinators excluded). Shared across all component instances to prevent
// duplicate API calls. null = not fetched yet.
export const accessibleManagedAgentsState = atom<ManagedAgentResponseModel[] | null>({
  key: 'AccessibleManagedAgents',
  default: null
});

export const accessibleManagedAgentsLoadingState = atom<boolean>({
  key: 'AccessibleManagedAgentsLoading',
  default: false
});

// True while a sub-agent roster selection is being persisted, so every surface
// that can toggle agents blocks further toggling until it lands.
export const agentRosterSavingState = atom<boolean>({
  key: 'AgentRosterSaving',
  default: false
});

// True once the server-side saved roster has been restored (or failed to),
// which tells consumers an empty selection genuinely means "nothing saved".
export const savedAgentRosterCheckedState = atom<boolean>({
  key: 'SavedAgentRosterChecked',
  default: false
});

// Global cache of available system prompts
// Shared across all component instances to prevent duplicate API calls
export const availableSystemPromptsState = atom<any[]>({
  key: 'AvailableSystemPrompts',
  default: []
});

// Selected system prompt name for current user
export const selectedSystemPromptState = atom<string | null>({
  key: 'SelectedSystemPrompt',
  default: null,
  effects: [
    ({ setSelf, onSet }) => {
      // Load from localStorage on initialization (global key)
      const storageKey = 'chainlit-selected-system-prompt';
      const savedValue = localStorage.getItem(storageKey);
      if (savedValue !== null && savedValue !== 'undefined' && savedValue !== '') {
        setSelf(savedValue);
      }

      // Save to localStorage whenever the value changes
      onSet((newValue) => {
        const storageKey = 'chainlit-selected-system-prompt';
        if (newValue === undefined || newValue === null) {
          localStorage.removeItem(storageKey);
        } else {
          localStorage.setItem(storageKey, newValue);
        }
      });
    }
  ]
});

// Global flags to track if data has been fetched (persists across component remounts)
// Using module-level variables ensures these persist for the entire app session
let _hasFetchedSources = false;
let _hasFetchedModels = false;
let _hasFetchedUserSelection = false;
let _hasFetchedCurrentAgent = false;
let _hasSyncedSources = false;
let _hasSyncedModel = false;
let _hasAutoSelectedSources = false;
let _hasAutoSelectedModel = false;
let _hasFetchedSystemPrompts = false;
let _hasFetchedUserSystemPrompt = false;
let _hasSyncedPrompt = false;

export const getHasFetchedSources = () => _hasFetchedSources;
export const setHasFetchedSources = (value: boolean) => { _hasFetchedSources = value; };
export const getHasFetchedModels = () => _hasFetchedModels;
export const setHasFetchedModels = (value: boolean) => { _hasFetchedModels = value; };
export const getHasFetchedUserSelection = () => _hasFetchedUserSelection;
export const setHasFetchedUserSelection = (value: boolean) => { _hasFetchedUserSelection = value; };
export const getHasFetchedCurrentAgent = () => _hasFetchedCurrentAgent;
export const setHasFetchedCurrentAgent = (value: boolean) => { _hasFetchedCurrentAgent = value; };
export const getHasSyncedSources = () => _hasSyncedSources;
export const setHasSyncedSources = (value: boolean) => { _hasSyncedSources = value; };
export const getHasSyncedModel = () => _hasSyncedModel;
export const setHasSyncedModel = (value: boolean) => { _hasSyncedModel = value; };
export const getHasAutoSelectedSources = () => _hasAutoSelectedSources;
export const setHasAutoSelectedSources = (value: boolean) => { _hasAutoSelectedSources = value; };
export const getHasAutoSelectedModel = () => _hasAutoSelectedModel;
export const setHasAutoSelectedModel = (value: boolean) => { _hasAutoSelectedModel = value; };
export const getHasFetchedSystemPrompts = () => _hasFetchedSystemPrompts;
export const setHasFetchedSystemPrompts = (value: boolean) => { _hasFetchedSystemPrompts = value; };
export const getHasFetchedUserSystemPrompt = () => _hasFetchedUserSystemPrompt;
export const setHasFetchedUserSystemPrompt = (value: boolean) => { _hasFetchedUserSystemPrompt = value; };
export const getHasSyncedPrompt = () => _hasSyncedPrompt;
export const setHasSyncedPrompt = (value: boolean) => { _hasSyncedPrompt = value; };
